/**
 * Credit grants (expiring credit) and usage refunds as the portal drives them. Expiry is
 * picked as an IST date-time and sent as the server's UTC LocalDateTime, like every other
 * cut-off in billing. The server is the authority on terms (400) and on what may be voided
 * or refunded (409); the forms show its message.
 */
import {
  AddCreditsRequest,
  BillingType,
  CreditGrantDTO,
  CreditSource,
  CreditTransactionDTO,
  GrantStatus,
  TransactionType,
} from '../../types/billing';
import { istNowLocal, istToServerUtc } from './closeBooksRules';

/** The server's default priority: lower is drawn first. */
export const DEFAULT_GRANT_PRIORITY = 100;

export interface AddCreditsForm {
  organizationId: string;
  amount: number | string;
  description?: string;
  source: CreditSource;
  /** IST `datetime-local`; blank = never expires. */
  expiresIst: string;
  /** Blank = the server's default. */
  priority: string;
}

/**
 * The POST /credits/add body. Only what differs from the server's defaults is sent, so
 * an operator who touches nothing sends exactly the old body.
 */
export const addCreditsBody = (
  f: AddCreditsForm,
  now: Date = new Date(),
): { body: AddCreditsRequest } | { error: string } => {
  const amount = Number(f.amount);
  if (!Number.isFinite(amount) || amount <= 0) return { error: 'Amount must be more than 0' };
  const body: AddCreditsRequest = { organizationId: f.organizationId, amount };
  if (f.description && f.description.trim()) body.description = f.description.trim();
  if (f.source && f.source !== 'PURCHASE') body.source = f.source;
  if (f.expiresIst && f.expiresIst.trim()) {
    const utc = istToServerUtc(f.expiresIst);
    if (!utc) return { error: 'Pick the expiry date and time' };
    if (f.expiresIst <= istNowLocal(now)) return { error: 'The expiry is in the past' };
    body.expiresAt = utc;
  }
  if (f.priority !== undefined && String(f.priority).trim() !== '') {
    const p = Number(f.priority);
    if (!Number.isInteger(p) || p < 0) return { error: 'Priority is a whole number, 0 or more' };
    if (p !== DEFAULT_GRANT_PRIORITY) body.priority = p;
  }
  return { body };
};

/**
 * Only an untouched ACTIVE grant can be voided; a partly used one would claw back credit
 * already spent, which the server refuses (409).
 */
export const canVoidGrant = (g: Pick<CreditGrantDTO, 'status' | 'originalAmount' | 'remainingAmount'>): boolean =>
  g.status === 'ACTIVE' && Number(g.remainingAmount) === Number(g.originalAmount);

/** A usage charge on a prepaid account; the server refuses postpaid and invoiced rows. */
export const isRefundable = (
  t: Pick<CreditTransactionDTO, 'transactionType'>,
  billingType?: BillingType | string | null,
): boolean => t.transactionType === TransactionType.DEBIT_USAGE && billingType !== BillingType.POSTPAID;

export const grantStatusClass = (s?: GrantStatus | string | null): string =>
  s === 'ACTIVE'
    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
    : s === 'EXPIRED'
      ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
      : s === 'VOID'
        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';

/** The expiring-window days the server accepts. */
export const clampDays = (n: number): number => Math.min(365, Math.max(1, Math.round(Number.isFinite(n) ? n : 30)));
